import React, { useEffect, useState } from "react";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { Box, Stack, Theme } from "@mui/material";
import { FilterModal } from "../FilterModal";
import { FilterItemButton } from "../../components/Button/FilterItemButton";
import { ClientResponse, IPagination, IQueryPerams } from "../../config/types";
import { SideBarSearchInput } from "../../components/Layout/SideBarSearchInput";
import { formatSnakeCaseToReadable } from "../../utils/common";

interface FilterProps<T> {
  ftn: (params: IQueryPerams) => Promise<ClientResponse<T>>;
  updateQuerySet: (query: React.SetStateAction<IQueryPerams>) => void;
  updateDataSet: (data: T, pagination?: IPagination) => void;
}

interface IAssignmentRejectionFilter {
  asset_brand?: string;
  asset_serial_number?: string;
  asset_embossment_number?: string;
  user_email?: string;
  reason?: string;
  search?: string;
}

export const AssignmentRejectionsLogsFilter = <T,>({
  ftn,
  updateQuerySet,
  updateDataSet,
}: FilterProps<T>) => {
  const [loading, setLoading] = useState(true);

  const { form, updateInputField, updateForm, onFormSubmit, getFormValues } =
    useDmsForm<IAssignmentRejectionFilter>({
      asset_brand: "",
      asset_serial_number: "",
      asset_embossment_number: "",
      user_email: "",
      reason: "",
      search: "",
    });

  const resetFilter = <K extends keyof IAssignmentRejectionFilter>(key: K) => {
    updateForm((p) => {
      const state = {
        ...p,
        [key]: { value: "" },
      };
      updateQuerySet((prev) => ({ ...prev, [key]: "" }));
      return state;
    });
  };

  useEffect(() => {
    const { data } = onFormSubmit();
    updateQuerySet((p) => ({ ...p, ...data }));
    const filterRequest = async () => {
      setLoading(true);
      const res = await ftn(data);
      if (res.data && updateDataSet) {
        updateDataSet(res.data, res.pagination);
      }
      setLoading(false);
    };
    filterRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.asset_brand?.value,
    form.asset_serial_number?.value,
    form.asset_embossment_number?.value,
    form.user_email?.value,
    form.reason?.value,
    form.search?.value,
  ]);

  return (
    <Stack direction={"row"} justifyContent={"space-between"} width={"100%"}>
      <Stack direction={"row"} spacing={2}>
        <FilterModal label="Filters" loading={loading} closeOnoutSideClick>
          <Stack direction={"column"} spacing={2}>
            <SideBarSearchInput
              label="Asset Embossment Number"
              placeholder="Search by asset embossment number ..."
              value={form.asset_embossment_number?.value || ""}
              handleChange={updateInputField("asset_embossment_number")}
            />
            <SideBarSearchInput
              label="User Email"
              placeholder="Search by user email ..."
              value={form.user_email?.value || ""}
              handleChange={updateInputField("user_email")}
            />
          </Stack>
        </FilterModal>
        <Box sx={styles.filterLayout}>
          {Object.entries(getFormValues())
            .filter(
              ([_, value]) =>
                typeof value === "string" && value.trim() !== ""
            )
            .map(([key, value]) => (
              <FilterItemButton
                key={key}
                label={`By ${formatSnakeCaseToReadable(key)}`}
                tip={value.toString()}
                height={40}
                onClick={() => resetFilter(key as keyof IAssignmentRejectionFilter)}
              />
            ))}
        </Box>
      </Stack>
      <Box width={250}>
        <SideBarSearchInput
          placeholder="Search by serial number ..."
          value={form.asset_serial_number?.value || ""}
          handleChange={updateInputField("asset_serial_number")}
        />
      </Box>
    </Stack>
  );
};

const styles = {
  filterLayout: (theme: Theme) => ({
    width: 1000,
    [theme.breakpoints.down("xl")]: {
      width: 570,
    },
  }),
};